import {Accordion} from "../Accordion/Accordion"
import React from "react"
import {
	AnnualIncome,
	BusinessAccountPurpose,
	BusinessIndustry,
	BusinessSourceOfFunds,
	BusinessVertical,
	IndividualAccountPurpose,
	IndividualSourceOfFunds,
	IndividualTransactionVolume,
	Occupation,
	Profession,
	SoleProprietorshipAnnualRevenue,
	SoleProprietorshipNumberOfEmployees,
	SolePropTransactionVolume,
	SourceOfIncome,
} from "../../resources/application"
import {UpdateIndividualCustomerAttributes} from "../../resources/customer"
import {isUndefined} from "lodash"
import {EditableOccupationField} from "../ApplicationAdditionalInformationFields/OccupationField"
import {EditableSourceOfIncomeField} from "../ApplicationAdditionalInformationFields/SourceOfIncomeField"
import {EditableAnnualIncomeField} from "../ApplicationAdditionalInformationFields/AnnualIncomeField"
import {EditableBusinessVerticalField} from "../ApplicationAdditionalInformationFields/BusinessVerticalField"
import {EditableSolePropNumberOfEmployeesField} from "../ApplicationAdditionalInformationFields/NumberOfEmployeesField"
import {EditableSolePropAnnualRevenueField} from "../ApplicationAdditionalInformationFields/AnnualRevenueField"
import {EditableWebsiteField} from "../EditableWebsiteField/EditableWebsiteField"
import {EditableIndividualSourceOfFundsField} from "../ApplicationAdditionalInformationFields/IndividualSourceOfFundsField"
import {EditableIndividualAccountPurposeField} from "../ApplicationAdditionalInformationFields/IndividualAccountPurposeField"
import {EditableIndividualTransactionVolumeField} from "../ApplicationAdditionalInformationFields/IndividualTransactionVolumeField"
import {EditableSolePropTransactionVolumeField} from "../ApplicationAdditionalInformationFields/SolePropTransactionVolumeField"
import {EditableProfessionField} from "../ApplicationAdditionalInformationFields/ProfessionField"
import {EditableBusinessIndustryField} from "../ApplicationAdditionalInformationFields/BusinessIndustryField"
import {EditableBusinessSourceOfFundsField} from "../ApplicationAdditionalInformationFields/BusinessSourceOfFundsField"
import {EditableBusinessAccountPurposeField} from "../ApplicationAdditionalInformationFields/BusinessAccountPurposeField"
import {EditableUsNexusField} from "../ApplicationAdditionalInformationFields/UsNexusField"
import {EditableCountriesOfOperationField} from "../ApplicationAdditionalInformationFields/CountriesOfOperation"
import {OptionalEditable, EditableOptionalString} from "../HorizontalField/EditableHorizontalField"

export function IndividualAdditionalInformationFields({
	occupation,
	setOccupation,
	sourceOfIncome,
	setSourceOfIncome,
	annualIncome,
	setAnnualIncome,
	businessVertical,
	setBusinessVertical,
	website,
	setWebsite,
	numberOfEmployees,
	setNumberOfEmployees,
	annualRevenue,
	setAnnualRevenue,
	isSoleProp,
	individualSourceOfFunds,
	setIndividualSourceOfFunds,
	businessSourceOfFunds,
	setBusinessSourceOfFunds,
	sourceOfFundsDescription,
	setSourceOfFundsDescription,
	individualAccountPurpose,
	setIndividualAccountPurpose,
	businessAccountPurpose,
	setBusinessAccountPurpose,
	accountPurposeDetail,
	setAccountPurposeDetail,
	individualTransactionVolume,
	setIndividualTransactionVolume,
	solePropTransactionVolume,
	setSolePropTransactionVolume,
	transactionVolumeDescription,
	setTransactionVolumeDescription,
	profession,
	setProfession,
	businessIndustry,
	setBusinessIndustry,
	usNexus,
	setUsNexus,
	countriesOfOperation,
	setCountriesOfOperation,
	businessDescription,
	setBusinessDescription,
	isEditing,
	setIsEditing,
	isUpdating,
	isAllowed,
	updateFunction,
	refresh,
	isArchived,
}: {
	occupation?: Occupation
	setOccupation: (value: Occupation) => void
	sourceOfIncome?: SourceOfIncome
	setSourceOfIncome: (value: SourceOfIncome) => void
	annualIncome?: AnnualIncome
	setAnnualIncome: (value: AnnualIncome) => void
	businessVertical?: BusinessVertical
	setBusinessVertical: (value: BusinessVertical) => void
	website?: string
	setWebsite: (value: string) => void
	numberOfEmployees?: SoleProprietorshipNumberOfEmployees
	setNumberOfEmployees: (value: SoleProprietorshipNumberOfEmployees) => void
	annualRevenue?: SoleProprietorshipAnnualRevenue
	setAnnualRevenue: (value: SoleProprietorshipAnnualRevenue) => void
	isSoleProp: boolean
	individualSourceOfFunds?: IndividualSourceOfFunds
	setIndividualSourceOfFunds: (value: IndividualSourceOfFunds) => void
	businessSourceOfFunds?: BusinessSourceOfFunds
	setBusinessSourceOfFunds: (value: BusinessSourceOfFunds) => void
	sourceOfFundsDescription?: string
	setSourceOfFundsDescription: (value: string | undefined) => void
	individualAccountPurpose?: IndividualAccountPurpose
	setIndividualAccountPurpose: (value: IndividualAccountPurpose) => void
	businessAccountPurpose?: BusinessAccountPurpose
	setBusinessAccountPurpose: (value: BusinessAccountPurpose) => void
	accountPurposeDetail?: string
	setAccountPurposeDetail: (value: string | undefined) => void
	individualTransactionVolume?: IndividualTransactionVolume
	setIndividualTransactionVolume: (value: IndividualTransactionVolume) => void
	solePropTransactionVolume?: SolePropTransactionVolume
	setSolePropTransactionVolume: (value: SolePropTransactionVolume) => void
	transactionVolumeDescription?: string
	setTransactionVolumeDescription: (value: string | undefined) => void
	profession?: Profession
	setProfession: (value: Profession) => void
	businessIndustry?: BusinessIndustry
	setBusinessIndustry: (value: BusinessIndustry) => void
	usNexus?: string[]
	setUsNexus: (value: string[]) => void
	countriesOfOperation?: string[]
	setCountriesOfOperation: (value: string[]) => void
	businessDescription?: string
	setBusinessDescription: (value: string | undefined) => void
	isEditing: string | undefined
	setIsEditing: (value: string | undefined) => void
	isUpdating: boolean
	isAllowed: boolean
	updateFunction: (attributes: UpdateIndividualCustomerAttributes) => void
	refresh: () => void
	isArchived: boolean
}) {
	const archivedClassName = isArchived ? "archived-field" : ""

	return (
		<Accordion title={"Additional Information"} theme={"light"} noContentPadding={true}>
			<EditableOccupationField
				occupation={occupation}
				setOccupation={setOccupation}
				isEditing={isEditing === "Occupation"}
				isDisabled={!isUndefined(isEditing)}
				isAllowed={isAllowed}
				onStartEdit={() => setIsEditing("Occupation")}
				onCancel={refresh}
				update={(value) => updateFunction({occupation: value})}
				className={archivedClassName}
			/>
			<EditableSourceOfIncomeField
				sourceOfIncome={sourceOfIncome}
				setSourceOfIncome={setSourceOfIncome}
				isEditing={isEditing === "Source of Income"}
				isDisabled={!isUndefined(isEditing)}
				isAllowed={isAllowed}
				onStartEdit={() => setIsEditing("Source of Income")}
				onCancel={refresh}
				update={(value) => updateFunction({sourceOfIncome: value})}
				className={archivedClassName}
			/>
			<EditableAnnualIncomeField
				annualIncome={annualIncome}
				setAnnualIncome={setAnnualIncome}
				isEditing={isEditing === "Annual Income"}
				isDisabled={!isUndefined(isEditing)}
				isAllowed={isAllowed}
				onStartEdit={() => setIsEditing("Annual Income")}
				onCancel={refresh}
				update={(value) => updateFunction({annualIncome: value})}
				className={archivedClassName}
			/>
			<EditableBusinessVerticalField
				businessVertical={businessVertical}
				setBusinessVertical={setBusinessVertical}
				isEditing={isEditing === "Business Vertical"}
				isDisabled={!isUndefined(isEditing)}
				isAllowed={isAllowed}
				onStartEdit={() => setIsEditing("Business Vertical")}
				onCancel={refresh}
				update={(value) => updateFunction({businessVertical: value})}
				className={archivedClassName}
			/>
			<EditableWebsiteField
				website={website}
				setWebsite={setWebsite}
				isEditing={isEditing === "Website"}
				onStartEdit={() => setIsEditing("Website")}
				onCancel={refresh}
				update={(value) => updateFunction({website: value})}
				isArchived={isArchived}
				isUpdating={isUpdating}
				isDisabled={!isUndefined(isEditing)}
				isAllowed={isAllowed}
			/>
			<EditableSolePropNumberOfEmployeesField
				numberOfEmployees={numberOfEmployees}
				setNumberOfEmployees={setNumberOfEmployees}
				isEditing={isEditing === "Number of Employees"}
				isDisabled={!isUndefined(isEditing)}
				isAllowed={isAllowed}
				onStartEdit={() => setIsEditing("Number of Employees")}
				onCancel={refresh}
				update={(value) => updateFunction({numberOfEmployees: value})}
				className={archivedClassName}
			/>
			<EditableSolePropAnnualRevenueField
				annualRevenue={annualRevenue}
				setAnnualRevenue={setAnnualRevenue}
				isEditing={isEditing === "Annual Revenue"}
				isDisabled={!isUndefined(isEditing)}
				isAllowed={isAllowed}
				onStartEdit={() => setIsEditing("Annual Revenue")}
				onCancel={refresh}
				update={(value) => updateFunction({annualRevenue: value})}
				className={archivedClassName}
			/>

			{!isSoleProp && (
				<>
					<EditableIndividualSourceOfFundsField
						sourceOfFunds={individualSourceOfFunds}
						setSourceOfFunds={setIndividualSourceOfFunds}
						isEditing={isEditing === "IndividualSourceOfFunds"}
						isDisabled={!isUndefined(isEditing)}
						isAllowed={isAllowed}
						onStartEdit={() => setIsEditing("IndividualSourceOfFunds")}
						onCancel={refresh}
						update={(v) => updateFunction({sourceOfFunds: v})}
						className={archivedClassName}
					/>
					<OptionalEditable
						label="Source of Funds Description"
						isEditing={isEditing === "SourceOfFundsDescription"}
						isDisabled={!isUndefined(isEditing)}
						isAllowed={isAllowed}
						isUpdating={isUpdating}
						onStartEdit={() => setIsEditing("SourceOfFundsDescription")}
						onCancel={refresh}
						setValue={setSourceOfFundsDescription}
						value={sourceOfFundsDescription}
						initial={sourceOfFundsDescription}
						update={() => updateFunction({sourceOfFundsDescription})}
						className={archivedClassName}
					>
						{EditableOptionalString}
					</OptionalEditable>
					<EditableIndividualAccountPurposeField
						accountPurpose={individualAccountPurpose}
						setAccountPurpose={setIndividualAccountPurpose}
						isEditing={isEditing === "IndividualAccountPurpose"}
						isDisabled={!isUndefined(isEditing)}
						isAllowed={isAllowed}
						onStartEdit={() => setIsEditing("IndividualAccountPurpose")}
						onCancel={refresh}
						update={(v) => updateFunction({accountPurpose: v})}
						className={archivedClassName}
					/>
					<OptionalEditable
						label="Account Purpose Detail"
						isEditing={isEditing === "AccountPurposeDetail"}
						isDisabled={!isUndefined(isEditing)}
						isAllowed={isAllowed}
						isUpdating={isUpdating}
						onStartEdit={() => setIsEditing("AccountPurposeDetail")}
						onCancel={refresh}
						setValue={setAccountPurposeDetail}
						value={accountPurposeDetail}
						initial={accountPurposeDetail}
						update={() => updateFunction({accountPurposeDetail})}
						className={archivedClassName}
					>
						{EditableOptionalString}
					</OptionalEditable>
					<EditableIndividualTransactionVolumeField
						transactionVolume={individualTransactionVolume}
						setTransactionVolume={setIndividualTransactionVolume}
						isEditing={isEditing === "IndividualTransactionVolume"}
						isDisabled={!isUndefined(isEditing)}
						isAllowed={isAllowed}
						onStartEdit={() => setIsEditing("IndividualTransactionVolume")}
						onCancel={refresh}
						update={(v) => updateFunction({transactionVolume: v})}
						className={archivedClassName}
					/>
					<OptionalEditable
						label="Transaction Volume Description"
						isEditing={isEditing === "TransactionVolumeDescription"}
						isDisabled={!isUndefined(isEditing)}
						isAllowed={isAllowed}
						isUpdating={isUpdating}
						onStartEdit={() => setIsEditing("TransactionVolumeDescription")}
						onCancel={refresh}
						setValue={setTransactionVolumeDescription}
						value={transactionVolumeDescription}
						initial={transactionVolumeDescription}
						update={() => updateFunction({transactionVolumeDescription})}
						className={archivedClassName}
					>
						{EditableOptionalString}
					</OptionalEditable>
					<EditableProfessionField
						profession={profession}
						setProfession={setProfession}
						isEditing={isEditing === "Profession"}
						isDisabled={!isUndefined(isEditing)}
						isAllowed={isAllowed}
						onStartEdit={() => setIsEditing("Profession")}
						onCancel={refresh}
						update={(v) => updateFunction({profession: v})}
						className={archivedClassName}
					/>
				</>
			)}

			{isSoleProp && (
				<>
					<EditableBusinessIndustryField
						businessIndustry={businessIndustry}
						setBusinessIndustry={setBusinessIndustry}
						isEditing={isEditing === "BusinessIndustry"}
						isDisabled={!isUndefined(isEditing)}
						isAllowed={isAllowed}
						onStartEdit={() => setIsEditing("BusinessIndustry")}
						onCancel={refresh}
						update={(v) => updateFunction({businessIndustry: v})}
						className={archivedClassName}
					/>
					<EditableBusinessSourceOfFundsField
						sourceOfFunds={businessSourceOfFunds}
						setSourceOfFunds={setBusinessSourceOfFunds}
						isEditing={isEditing === "BusinessSourceOfFunds"}
						isDisabled={!isUndefined(isEditing)}
						isAllowed={isAllowed}
						onStartEdit={() => setIsEditing("BusinessSourceOfFunds")}
						onCancel={refresh}
						update={(v) => updateFunction({sourceOfFunds: v})}
						className={archivedClassName}
					/>
					<OptionalEditable
						label="Source of Funds Description"
						isEditing={isEditing === "SourceOfFundsDescriptionSoleProp"}
						isDisabled={!isUndefined(isEditing)}
						isAllowed={isAllowed}
						isUpdating={isUpdating}
						onStartEdit={() => setIsEditing("SourceOfFundsDescriptionSoleProp")}
						onCancel={refresh}
						setValue={setSourceOfFundsDescription}
						value={sourceOfFundsDescription}
						initial={sourceOfFundsDescription}
						update={() => updateFunction({sourceOfFundsDescription})}
						className={archivedClassName}
					>
						{EditableOptionalString}
					</OptionalEditable>
					<EditableBusinessAccountPurposeField
						accountPurpose={businessAccountPurpose}
						setAccountPurpose={setBusinessAccountPurpose}
						isEditing={isEditing === "BusinessAccountPurpose"}
						isDisabled={!isUndefined(isEditing)}
						isAllowed={isAllowed}
						onStartEdit={() => setIsEditing("BusinessAccountPurpose")}
						onCancel={refresh}
						update={(v) => updateFunction({accountPurpose: v})}
						className={archivedClassName}
					/>
					<OptionalEditable
						label="Account Purpose Detail"
						isEditing={isEditing === "AccountPurposeDetailSoleProp"}
						isDisabled={!isUndefined(isEditing)}
						isAllowed={isAllowed}
						isUpdating={isUpdating}
						onStartEdit={() => setIsEditing("AccountPurposeDetailSoleProp")}
						onCancel={refresh}
						setValue={setAccountPurposeDetail}
						value={accountPurposeDetail}
						initial={accountPurposeDetail}
						update={() => updateFunction({accountPurposeDetail})}
						className={archivedClassName}
					>
						{EditableOptionalString}
					</OptionalEditable>
					<EditableSolePropTransactionVolumeField
						transactionVolume={solePropTransactionVolume}
						setTransactionVolume={setSolePropTransactionVolume}
						isEditing={isEditing === "SolePropTransactionVolume"}
						isDisabled={!isUndefined(isEditing)}
						isAllowed={isAllowed}
						onStartEdit={() => setIsEditing("SolePropTransactionVolume")}
						onCancel={refresh}
						update={(v) => updateFunction({transactionVolume: v})}
						className={archivedClassName}
					/>
					<OptionalEditable
						label="Transaction Volume Description"
						isEditing={isEditing === "TransactionVolumeDescriptionSoleProp"}
						isDisabled={!isUndefined(isEditing)}
						isAllowed={isAllowed}
						isUpdating={isUpdating}
						onStartEdit={() => setIsEditing("TransactionVolumeDescriptionSoleProp")}
						onCancel={refresh}
						setValue={setTransactionVolumeDescription}
						value={transactionVolumeDescription}
						initial={transactionVolumeDescription}
						update={() => updateFunction({transactionVolumeDescription})}
						className={archivedClassName}
					>
						{EditableOptionalString}
					</OptionalEditable>
					<EditableUsNexusField
						usNexus={usNexus}
						setUsNexus={setUsNexus}
						isEditing={isEditing === "UsNexus"}
						isDisabled={!isUndefined(isEditing)}
						isAllowed={isAllowed}
						isUpdating={false}
						onStartEdit={() => setIsEditing("UsNexus")}
						onCancel={refresh}
						update={(v) => updateFunction({usNexus: v})}
						className={archivedClassName}
					/>
					<EditableCountriesOfOperationField
						countriesOfOperation={countriesOfOperation}
						setCountriesOfOperation={setCountriesOfOperation}
						isEditing={isEditing === "Countries of Operation"}
						isDisabled={!isUndefined(isEditing)}
						isAllowed={isAllowed}
						isUpdating={false}
						onStartEdit={() => setIsEditing("Countries of Operation")}
						onCancel={refresh}
						update={() => updateFunction({countriesOfOperation})}
						className={archivedClassName}
					/>
					<OptionalEditable
						label="Business Description"
						isEditing={isEditing === "BusinessDescription"}
						isDisabled={!isUndefined(isEditing)}
						isAllowed={isAllowed}
						isUpdating={isUpdating}
						onStartEdit={() => setIsEditing("BusinessDescription")}
						onCancel={refresh}
						setValue={setBusinessDescription}
						value={businessDescription}
						initial={businessDescription}
						update={() => updateFunction({businessDescription})}
						className={archivedClassName}
					>
						{EditableOptionalString}
					</OptionalEditable>
				</>
			)}
		</Accordion>
	)
}
